import type { Access, TenantRole } from './roles.ts';
/**
 * Formatos das respostas da API, compartilhados entre a API e o painel.
 * Só tipos: nada aqui existe em tempo de execução.
 */

export type MatchStatus = 'pending' | 'matched' | 'ambiguous' | 'unmatched' | 'no_gtin' | 'kit' | 'manual';
/** Segmento do cliente e da ficha. Muda campos, colunas e regras exibidas. */
export type Vertical = 'pharmacy' | 'general';
export type GtinStatus = 'valid' | 'invalid_format' | 'invalid_length' | 'invalid_check_digit' | 'internal_code';

export type Plan = 'trial' | 'paid' | 'internal';

/** Equipe Berytus (painel master). */
export type PlatformRole = 'superadmin' | 'support';

/** Período de teste: dias restantes, limites e uso. null para quem não está em teste. */
export interface TrialInfo {
  endsAt: string;
  daysLeft: number;
  expired: boolean;
  limits: { products: number | null; listingsPerChannel: number | null };
  usage: { products: number };
}

export interface Me {
  user: { id: string; name: string; email: string; emailVerified: boolean; platformRole: PlatformRole | null };
  /** Sessão aberta pela equipe Berytus para ver a conta (só leitura). */
  impersonating: { byName: string } | null;
  preferences: Preferences;
  tenant: { id: string; slug: string; name: string; vertical: Vertical; role: TenantRole; plan: Plan; /** Marcado como farmacêutico (com CRF): decide receitas. */ pharmacist: boolean; /** Permissões (grupos somados). */ access: Access };
  trial: TrialInfo | null;
  tenants: { id: string; slug: string; name: string; vertical: Vertical }[];
}

export interface CatalogSummary {
  total: number;
  active: number;
  inactive: number;
  withStock: number;
  kits: number;
  byStatus: Record<MatchStatus, number>;
  invalidGtins: number;
  lastImportAt: string | null;
}

export interface CategoryCount {
  category: string;
  count: number;
}

export interface ProductRow {
  id: string;
  sku: string;
  title: string | null;
  category: string | null;
  gtin: string | null; // EAN do vínculo, em 13 dígitos quando possível
  matchStatus: MatchStatus;
  isKit: boolean;
  kitUnits: number | null;
  active: boolean;
  available: number;
  cost: string | null;
  pmc: string | null;
  imageUrl: string | null;
  /** Preço de venda por canal (mercadolivre, shopee, ifood). */
  channelPrices: Record<string, string>;
}

export interface Page<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
}

export interface ProductGtin {
  priority: number;
  source: string;
  rawValue: string;
  gtin: string | null;
  status: GtinStatus;
  usedForMatch: boolean;
}

/** Campos de farmácia da ficha (só quando vertical = 'pharmacy'). */
export interface PharmaFields {
  anvisaCode: string | null;
  activeIngredient: string | null;
  dosage: string | null;
  presentation: string | null;
  atcCode: string | null;
  drugClass: string | null;
  isMedicine: boolean;
  isOtc: boolean | null;
  isControlled: boolean;
  isAntibiotic: boolean;
  isThermolabile: boolean;
}

export interface CatalogFicha {
  id: string;
  title: string;
  vertical: Vertical;
  brand: string | null;
  manufacturer: string | null;
  description: string | null;
  pharma: PharmaFields | null;
  gtins: string[];
  images: string[];
}

export interface StoreStock {
  storeCode: string;
  storeName: string;
  quantity: number;
  safetyStock: number;
  available: number;
  updatedAt: string;
}

export interface KitSummary {
  id: string;
  sku: string;
  title: string;
  units: number;
  discountPct: string | null;
  available: number;
  cost: string | null;
  pmc: string | null;
  /** Preço no Mercado Livre (base × unidades − desconto, ou na mão). */
  meliPrice: string | null;
}

export interface ProductDetail {
  /** Atributos padrão do sistema (somente leitura, do catálogo mestre); null sem ficha conferida. */
  systemAttributes: SystemAttributes | null;
  id: string;
  sku: string;
  /** Capa: primeira foto própria do produto (null = usa a do catálogo central). */
  cover: string | null;
  title: string | null;
  department: string | null;
  category: string | null;
  subcategory: string | null;
  curve: string | null;
  active: boolean;
  matchStatus: MatchStatus;
  matchedGtin: string | null;
  isKit: boolean;
  kitUnits: number | null;
  kitDiscountPct: string | null;
  base: { id: string; sku: string; title: string | null } | null;
  cost: string | null;
  costNet: string | null;
  pmc: string | null;
  minMargin: string | null;
  fixedPrice: string | null;
  minPrice: string | null;
  maxPrice: string | null;
  flags: Record<string, boolean>;
  sourceUpdatedAt: string | null;
  updatedAt: string;
  gtins: ProductGtin[];
  /**
   * GTIN que vale para o produto (gtin14, no formato do mestre). corrected: chegou sem os zeros da frente e a Berytus
   * completou (received = o valor recebido); invalid: tem código, nenhum fecha — bloqueia o envio aos canais.
   */
  gtin: { ean: string | null; status: 'valid' | 'corrected' | 'invalid' | 'none'; received: string | null };
  ficha: CatalogFicha | null;
  stock: StoreStock[];
  kits: KitSummary[];
}

export interface CreateKitBody {
  units: number;
  title?: string;
  sku?: string;
  discountPct?: number;
}

export interface SyncJob {
  id: string;
  kind: string;
  status: 'queued' | 'running' | 'succeeded' | 'partial' | 'failed' | 'cancelled';
  stats: Record<string, unknown>;
  error: string | null;
  createdAt: string;
  startedAt: string | null;
  finishedAt: string | null;
  warnings: number;
}

export interface SyncEvent {
  id: number;
  level: 'info' | 'warn' | 'error';
  entityRef: string | null;
  message: string;
  data: unknown;
  createdAt: string;
}

export type ThemePref = 'system' | 'light' | 'dark';
export type Density = 'compact' | 'comfortable';

export interface TablePrefs {
  hidden?: string[]; // colunas escondidas
  sizes?: Record<string, number>; // largura de cada coluna, em px
  pageSize?: number;
}

export interface Preferences {
  theme?: ThemePref;
  density?: Density;
  sidebarCollapsed?: boolean;
  tables?: Record<string, TablePrefs>; // por tabela: 'catalog'...
}

export interface ApiError {
  error: string;
  message: string;
}

// ---------- Canais ----------

export type ChannelCode = 'mercadolivre' | 'shopee' | 'ifood' | '99food';
export type AccountStatus = 'pending' | 'active' | 'expired' | 'revoked' | 'error';
export type LinkStatus = 'pending' | 'linked' | 'ambiguous' | 'orphan' | 'ignored';
export type LinkMethod = 'seller_sku' | 'gtin' | 'title' | 'manual';

export interface ChannelAccountInfo {
  id: string;
  displayName: string | null;
  externalAccountId: string;
  status: AccountStatus;
  lastError: string | null;
  lastPullAt: string | null;
  pull: SyncJob | null; // última leitura de anúncios
  createdAt: string;
  /** Loja (mesmo CNPJ) cujo estoque esta conexão vende. */
  store: { id: string; code: string; name: string } | null;
}

export interface StoreRow {
  id: string;
  code: string;
  name: string;
  city: string | null;
  state: string | null;
  postalCode: string | null;
  active: boolean;
  notes: string | null;
  products: number;      // produtos com estoque gravado nesta loja
  connections: { id: string; channel: string; name: string | null }[];
}

export interface ChannelInfo {
  code: ChannelCode;
  name: string;
  kind: 'marketplace' | 'delivery';
  available: boolean; // integração já disponível na Berytus
  configured: boolean; // credenciais do app presentes no servidor
  configError: string | null; // por que não está configurado (ex.: chave com tamanho errado)
  /** Shopee: app em teste (sandbox) ou produção. */
  environment?: 'test' | 'live';
  accounts: ChannelAccountInfo[];
}

export interface ConnectResponse {
  url: string; // para onde levar o usuário
  redirectUri: string; // endereço de retorno cadastrado no app do canal
}

export interface CallbackBody {
  url?: string; // endereço completo copiado do navegador
  code?: string;
  state?: string;
}

export type CatalogLookupStatus = 'found' | 'not_found' | 'parent' | 'error';

export interface ChannelSummary {
  accounts: number;
  listings: { total: number; active: number; paused: number; closed: number; other: number };
  link: Record<Exclude<LinkStatus, 'ignored'>, number>;
  catalogListings: number;
  products: { active: number; withListing: number; withoutListing: number; withoutListingInStock: number };
  lastPull: SyncJob | null;
  catalog: { found: number; foundUnlisted: number; notFound: number; unchecked: number; lastLookup: SyncJob | null };
}

export interface ListingProductRef {
  id: string;
  sku: string;
  title: string | null;
  isKit: boolean;
}

export interface ChannelListingRow {
  id: string;
  externalId: string;
  variationId: string;
  variation: string | null;
  title: string | null;
  sellerSku: string | null;
  gtin: string | null;
  status: string | null;
  subStatus: string[];
  isCatalog: boolean;
  catalogId: string | null;
  listingType: string | null;
  price: string | null;
  availableQuantity: number | null;
  unitsPerPack: number | null;
  permalink: string | null;
  thumbnail: string | null;
  linkStatus: LinkStatus;
  linkMethod: LinkMethod | null;
  linkNote: string | null;
  product: ListingProductRef | null;
  suggestion: (ListingProductRef & { score: number; warning?: string | null }) | null; // para órfãos: produto com título parecido (warning: apresentação diferente)
  updatedAt: string | null;
  buyBox: { status: string | null; priceToWin: string | null; winnerPrice: string | null; marginPct: number | null; checkedAt: string | null } | null;
  marginPct: number | null; // margem do preço atual do anúncio, com as taxas do canal
  createdByBerytus: boolean;
  berytusPrice: string | null; // preço final da tabela da Berytus para o produto vinculado
  /** Estoque que a Berytus manda para este canal (null = produto sem tabela). */
  berytusStock?: number | null;
  alerts: ListingAlert[];
  /** Por que o título não bate com o produto ligado (conferência de título). */
  titleAlertDetail?: string | null;
}

export interface ChannelProductRow {
  id: string;
  sku: string;
  title: string | null;
  category: string | null;
  gtin: string | null;
  isKit: boolean;
  kitUnits: number | null;
  available: number;
  cost: string | null;
  listings: number; // anúncios ativos ou pausados
  /** Alertas antes de anunciar (ficha diferente, em revisão, SKU já anunciado, preço…), na conta escolhida. */
  alerts?: { code: string; detail: string }[];
  // null = não consultado. gtin = o EAN que achou a ficha (a busca vai em cascata: principal, 2º, 3º...)
  catalog: { status: CatalogLookupStatus; catalogProductId: string | null; name: string | null; gtin: string | null } | null;
  main: {
    listingId: string;
    externalId: string;
    title: string | null;
    status: string | null;
    price: string | null;
    availableQuantity: number | null;
    isCatalog: boolean;
    permalink: string | null;
  } | null;
}

export interface LinkBody {
  productId: string | null;
}

// ---------- Oportunidades (mais vendidos do Mercado Livre) ----------

export interface MarketRoot {
  id: string;
  name: string;
  tracked: boolean;
  suggested: boolean; // sugerida para o segmento do cliente
  status: 'idle' | 'queued' | 'running' | 'failed' | null;
  progress: { categories?: number; leaves?: number; ranked?: number; details?: number; phase?: string } | null;
  lastError: string | null;
  refreshedAt: string | null;
  ranked: number; // posições guardadas abaixo desta raiz
}

export interface MarketCategory {
  id: string;
  name: string;
  pathNames: string;
  depth: number;
  ranked: number;
}

/** missing: não tenho o produto · unlisted: tenho e não anuncio · listed: anuncio · unknown: sem EAN no ML para cruzar */
export type OpportunitySituation = 'missing' | 'unlisted' | 'listed' | 'unknown';

export interface OpportunityRow {
  categoryId: string;
  categoryPath: string;
  position: number;
  extType: 'PRODUCT' | 'ITEM' | 'USER_PRODUCT';
  extId: string;
  title: string | null;
  brand: string | null;
  gtin: string | null;
  price: string | null;
  thumbnail: string | null;
  permalink: string | null;
  isCatalog: boolean;
  situation: OpportunitySituation;
  product: { id: string; sku: string; title: string | null; available: number } | null;
  listing: { externalId: string; status: string | null; price: string | null; permalink: string | null } | null;
}

export interface OpportunityPage extends Page<OpportunityRow> {
  summary: Record<OpportunitySituation, number> & { total: number };
}

// ---------- Preço e estoque por canal ----------

export type PriceSource = 'manual' | 'buybox' | 'engine' | 'api' | 'default' | 'kit' | 'none';
export type PriceAlert = 'no_cost' | 'below_cost' | 'above_pmc' | 'low_margin' | 'no_price' | 'capped_at_pmc' | 'default_price';

export interface ChannelSettings {
  pricingMode: 'engine' | 'external'; // Berytus calcula | preço vem por API
  takeRatePct: number; // Clássico
  takeRatePremiumPct: number | null; // Premium; null = igual ao Clássico
  defaultListingType: 'gold_special' | 'gold_pro';
  extraPct: number;
  extraFixed: number;
  fixedFee: number;
  fixedFeeBelow: number | null;
  /** meli_table: custo fixo do Mercado Livre por faixa de preço; flat: o valor único acima. */
  fixedFeeMode: 'flat' | 'meli_table';
  defaultMarginPct: number;
  minMarginAlertPct: number;
  capAtPmc: boolean;
  rounding: 'none' | '90' | '99';
  stockMode: 'engine' | 'external';
  safetyStock: number;
  maxStock: number | null;
  stockPct: number; // % do estoque disponível que vai para o canal (100 = tudo)
  syncMode: 'off' | 'stock' | 'price' | 'price_stock'; // envio automático para o canal
  priceGuardPct: number; // preço que muda mais que este % não vai sozinho (0 = sem trava)
  /** Preço abaixo do mínimo da categoria no canal: envia o mínimo ('min') ou não envia ('skip'). */
  belowMinPrice?: 'min' | 'skip';
  /** Medicamentos com tarja (não MIP) liberados para o canal. Controlados nunca vão. */
  allowPrescription?: boolean;
  /** Shopee: texto do atributo Anvisa para não medicamento sem registro (ex.: "Isento de registro"); null = desligado. */
  shopeeAnvisaExempt?: string | null;
  lastSyncAt?: string | null;
  lastSyncStats?: ChannelSyncStats | null;
  updatedAt?: string;
}

/** Resultado da última rodada de envio de preço/estoque para o canal. */
export interface ChannelSyncStats {
  checked: number;
  sent: number;
  failed: number;
  skipped: Record<string, number>;
}

export interface PriceRule {
  id: string;
  scope: 'category' | 'product';
  category: string | null;
  product: { id: string; sku: string; title: string | null } | null;
  marginPct: number;
}

export interface ChannelSettingsResponse {
  accountId: string;
  settings: ChannelSettings;
  rules: PriceRule[];
}

export interface OfferRow {
  productId: string;
  sku: string;
  title: string | null;
  category: string | null;
  isKit: boolean;
  cost: string | null;
  pmc: string | null;
  marginRule: { pct: number; from: 'product' | 'category' | 'general' } | null;
  computedPrice: string | null;
  fixedPrice: string | null;
  fixedReason: 'manual' | 'buybox' | null;
  apiPrice: string | null;
  finalPrice: string | null;
  priceSource: PriceSource | null;
  marginPct: string | null;
  profit: number | null;
  available: number;
  finalStock: number | null;
  blockedBy?: string | null;
  stockOverride: number | null;
  alerts: PriceAlert[];
  listing: {
    id: string;
    externalId: string;
    status: string | null;
    price: string | null;
    availableQuantity: number | null;
    isCatalog: boolean;
    permalink: string | null;
    buyBoxStatus: string | null;
    buyBoxPrice: string | null;
    buyBoxMarginPct: number | null; // margem se acatar o buy box
  } | null;
}

export interface OfferPage extends Page<OfferRow> {
  accountId: string;
  summary: { products: number; withPrice: number; alerts: Partial<Record<PriceAlert, number>> };
}

export interface OfferPatch {
  fixedPrice?: number | null;
  reason?: 'manual' | 'buybox';
  stockOverride?: number | null;
}

export interface ApiOfferItem {
  sku: string;
  price?: number | null;
  stock?: number | null;
}

/** Alertas de um anúncio, comparando o preço que está no canal com o custo e o PMC do produto. */
export type ListingAlert = 'below_cost' | 'above_pmc' | 'low_margin' | 'buybox_lost' | 'price_out_of_sync' | 'stock_out_of_sync' | 'title_mismatch' | 'kit_mismatch' | 'measure_mismatch';

// ---------- Publicação de anúncios ----------

export type ListingType = 'gold_special' | 'gold_pro';
export type PublishBlock =
  | 'not_checked' | 'not_in_catalog' | 'parent_product' | 'no_category' | 'no_gtin'
  | 'no_price' | 'no_stock' | 'below_cost' | 'already_listed' | 'pending' | 'trial_limit' | 'kit_catalog' | 'kit_no_photo'
  | 'own_no_category' | 'own_missing_attributes' | 'own_no_photo' | 'channel_blocked' | 'gtin_invalid' | 'image_below_min' | 'image_unchecked' | 'controlled_344';

export interface PublishCandidate {
  productId: string;
  sku: string;
  title: string | null;
  catalogProductId: string | null;
  catalogName: string | null;
  categoryId: string | null;
  picture: string | null;
  listingType: ListingType;
  price: number | null;
  marginPct: number | null;
  quantity: number;
  blocks: PublishBlock[];
  warnings: ('low_margin' | 'default_price' | 'already_listed' | 'no_package')[];
  mode: 'catalog' | 'listing' | 'own';
  kitUnits: number | null;
  missing?: string[];
  categoryPath?: string | null;
  /** Alertas antes de anunciar: ficha diferente do produto, anúncio em revisão, SKU já anunciado, última tentativa recusada. */
  alerts?: { code: string; detail: string }[];
  /** Anúncios que já existem nesta conta para o produto (mesmo produto, mesmo SKU ou mesma ficha do catálogo). */
  existing?: ExistingListing[];
}

export interface ExistingListing { externalId: string; status: string | null; title: string | null; catalog: boolean; sku: string | null }

/** Produtos escolhidos um a um, ou todos os que batem com o filtro (encontrados no catálogo e sem anúncio). */
export interface PublishBody {
  productIds?: string[];
  /** attrs: atributos próprios, ex.: "controlado:true;antibiotico:false". */
  filter?: { category?: string; attrs?: string; alert?: string; stock?: 'in' };
  listingType?: ListingType;
  /** Publicar mesmo se já existir anúncio para o SKU nesta conta (vira aviso em vez de impedimento). */
  allowDuplicates?: boolean;
}

export interface PublishPreview {
  items: PublishCandidate[];
  ready: number;
  blocked: number;
  truncated: boolean;
  /** Conta do Mercado Livre liberada para anunciar? null quando não deu para consultar. */
  seller?: { canList: boolean; codes: string[]; message: string | null } | null;
}

export interface PublishResult {
  queued: number;
  skipped: number;
}

export interface PublicationRow {
  id: string;
  productId: string;
  sku: string;
  title: string | null;
  listingType: ListingType;
  price: string;
  quantity: number;
  status: 'queued' | 'sending' | 'published' | 'failed' | 'cancelled';
  externalId: string | null;
  externalStatus: string | null;
  permalink: string | null;
  error: string | null;
  createdAt: string;
  sentAt: string | null;
  createdBy: string | null;
}

// ---------- Cadastro e conta ----------

export interface SignupBody {
  cnpj: string;
  legalName: string; // razão social
  tradeName: string; // nome fantasia (aparece no painel)
  vertical: Vertical;
  name: string; // responsável
  email: string;
  phone?: string;
  password: string;
  acceptTerms: true;
}

export interface CnpjLookup {
  cnpj: string;
  legalName: string | null;
  tradeName: string | null;
  city: string | null;
  state: string | null;
  active: boolean | null;
}

export interface Company {
  id: string;
  cnpj: string | null;
  legalName: string | null;
  name: string;
  vertical: Vertical;
  phone: string | null;
  contactEmail: string | null;
  address: { street?: string; number?: string; complement?: string; district?: string; city?: string; state?: string; zip?: string };
  plan: Plan;
  trial: TrialInfo | null;
  createdAt: string;
  taxRegime: 'simples' | 'normal' | null;
  /** Pedidos e estoque: vazio = automático (reserva se a empresa tem integração por API, baixa se não tem). */
  orderStockMode: OrderStockMode | null;
  orderStockModeEffective: OrderStockMode;
  /** Link do XML da nota no ERP, com {chave} no lugar da chave de acesso. */
  invoiceXmlUrl?: string | null;
}

export type OrderStockMode = 'deduct' | 'reserve' | 'off';

export interface NewProductBody {
  sku: string;
  /** O cliente marcou como medicamento (fora do mestre: aguarda curadoria). */
  isMedicine?: boolean;
  title: string;
  gtin?: string;
  category?: string;
  brand?: string;
  cost?: number;
  pmc?: number;
  stock?: number;
}

// ---------- Painel master (equipe Berytus) ----------

/** Situação comercial da conta. */
export type AccountState = 'trial' | 'trial_expired' | 'paid' | 'internal' | 'suspended';

export interface AdminTenantRow {
  id: string;
  name: string;
  legalName: string | null;
  cnpj: string | null;
  vertical: Vertical;
  plan: Plan;
  state: AccountState;
  createdAt: string;
  /** Dias desde o cadastro. */
  ageDays: number;
  trialEndsAt: string | null;
  trialDaysLeft: number | null;
  suspendedAt: string | null;
  productLimit: number | null;
  listingLimit: number | null;
  signupSource: string | null;
  users: number;
  owner: { name: string; email: string; emailVerified: boolean } | null;
  lastLoginAt: string | null;
  products: number;
  channels: number;
  listings: number;
  published: number;
  /** Alimenta o catálogo mestre pela API: curadoria (corrige) ou colaborador (só preenche o vazio). */
  catalogRole?: 'curator' | 'contributor' | null;
}

export interface AdminStats {
  tenants: number;
  byState: Record<AccountState, number>;
  signups7d: number;
  signups30d: number;
  expiringIn7d: number;
  /** Funil das contas criadas pelo site. */
  funnel: { signedUp: number; verified: number; connected: number; published: number; paid: number };
  recent: AdminTenantRow[];
  /** Saúde do servidor: disco onde ficam as fotos e tamanho do banco (bytes). */
  server: {
    diskFree: number | null; diskTotal: number | null; dbBytes: number; mediaDriver: string;
    /** CPU agora (0–1, média de todos os núcleos em ~0,5 s), carga média de 1/5/15 min e núcleos; memória do servidor. */
    cpu?: number | null; load?: [number, number, number]; cores?: number; memTotal?: number; memFree?: number;
  };
}

export interface AdminUserRow {
  id: string;
  name: string;
  email: string;
  role: TenantRole;
  active: boolean;
  emailVerified: boolean;
  platformRole: PlatformRole | null;
  createdAt: string;
  lastLoginAt: string | null;
  /** Sessões abertas agora. */
  sessions: number;
}

export interface AuditEntry {
  id: string;
  at: string;
  actor: string | null;
  targetUser: string | null;
  action: string;
  details: Record<string, unknown>;
}

export interface AdminTenantDetail {
  tenant: AdminTenantRow;
  company: { phone: string | null; contactEmail: string | null; address: Company['address']; suspendedReason: string | null };
  users: AdminUserRow[];
  channels: { id: string; channel: string; name: string | null; status: string; createdAt: string; lastError: string | null }[];
  audit: AuditEntry[];
}

export interface AdminPlanBody {
  plan: Plan;
  /** Só no plano de teste. */
  trialEndsAt?: string | null;
  productLimit: number | null;
  listingLimit: number | null;
}

// ---------- Edição de anúncio ----------

/** Anúncio lido na hora do Mercado Livre, para a janela de edição. */
export interface ListingDetail {
  listingId: string;
  externalId: string;
  title: string;
  price: number;
  availableQuantity: number;
  soldQuantity: number | null;
  status: string;
  subStatus: string[];
  isCatalog: boolean;
  listingType: string;
  sellerSku: string | null;
  description: string | null;
  permalink: string | null;
  thumbnail: string | null;
  variations: { id: number; label: string; price: number; availableQuantity: number; sku: string | null }[];
}

export interface ListingUpdateBody {
  title?: string;
  price?: number;
  quantity?: number;
  sku?: string;
  description?: string;
  variations?: { id: number; price?: number; quantity?: number }[];
  status?: 'active' | 'paused' | 'closed';
}

export interface BulkStatusResult {
  ok: number;
  failed: { externalId: string; error: string }[];
}

// ---------- Cadastro completo do produto ----------

export type ContentFieldKey =
  | 'title' | 'brand' | 'department' | 'category' | 'subcategory'
  | 'netWeightKg' | 'grossWeightKg' | 'heightCm' | 'widthCm' | 'lengthCm' | 'eanInvoice'
  | 'manufacturer' | 'model' | 'description'
  | 'ncm' | 'cest' | 'taxSegment' | 'taxSituation' | 'originType' | 'originDetail' | 'fci' | 'exTipi' | 'taxRuleId' | 'taxUnit'
  | 'anvisaCode' | 'anvisaExemption'
  | 'handlingDays' | 'manufacturingDays' | 'warrantyMonths' | 'warrantyType';

export type ContentSourceKind = 'manual' | 'integration' | 'sheet' | 'api';
export interface FieldSourceInfo { src: ContentSourceKind; at: string; by?: string; byName?: string | null }

export interface MediaItem {
  id: string;
  /** Para exibir nas telas (relativo no armazenamento local). */
  url: string;
  /** Endereço público, o que vai para os marketplaces. */
  publicUrl: string;
  position: number;
  width: number | null;
  height: number | null;
  bytes: number;
  contentType: string;
  sourceUrl: string | null;
  /** Avisos de regra dos canais (ex.: menor que 500 px). */
  warnings: string[];
}

export type AttributeType = 'text' | 'number' | 'boolean' | 'enum' | 'date';
export interface AttributeDef {
  id: string;
  key: string;
  label: string;
  dataType: AttributeType;
  options: string[];
  level: 'product' | 'sku';
  unit: string | null;
  filterable: boolean;
  sentToChannels: boolean;
  channelMap: Record<string, string>;
  position: number;
  /** Quantos produtos têm valor. */
  used: number;
  /** Atributo padrão da Berytus (MIP, controlado, psicotrópico): não pode ser apagado nem mudar de tipo. */
  system?: boolean;
}

export interface TaxRule {
  id: string;
  name: string;
  description: string | null;
  channelIds: Record<string, string | number>;
  used: number;
}

export type TaxRegime = 'simples' | 'normal';

export interface ProductContent {
  productId: string;
  sku: string;
  isKit: boolean;
  kitUnits: number | null;
  /** Kit: o cadastro é o do produto base. */
  base: { id: string; sku: string; title: string | null } | null;
  groupId: string | null;
  values: Record<ContentFieldKey, string | number | null>;
  sources: Record<string, FieldSourceInfo>;
  /** Valores do catálogo central, herdados quando o campo do cliente está vazio. */
  central: { title: string | null; brand: string | null; manufacturer: string | null; description: string | null; anvisaCode: string | null; images: string[] } | null;
  /** Nome com que o item vai aos canais e aparece nas telas: medicamento vinculado ao mestre usa o nome da ficha. */
  channelTitle: string | null;
  /** Medicamento vinculado ao mestre: campos travados (regra lock_medicine_fields; observe = só avisa). */
  medicineLock: { mode: 'off' | 'observe' | 'enforce'; fields: string[]; master: { title: string; brand: string | null; manufacturer: string | null; anvisaCode: string | null }; dueBusinessDays: number } | null;
  /** pending_curation: marcado como medicamento e fora do mestre (aguarda a curadoria da Berytus). */
  curationStatus: string | null;
  attributes: Record<string, string | number | boolean>;
  attributeDefs: AttributeDef[];
  media: { product: MediaItem[]; sku: MediaItem[] };
  taxRegime: TaxRegime | null;
  taxRules: { id: string; name: string }[];
  locations: { storeCode: string; storeName: string; location: string | null }[];
  /** Combinações que merecem atenção (não bloqueiam). */
  warnings: { field: string; message: string }[];
}

export type ContentUpdateBody = Partial<Record<ContentFieldKey, string | number | null>> & {
  attributes?: Record<string, string | number | boolean | null>;
  locations?: { storeCode: string; location: string | null }[];
};

export interface MediaUploadBody {
  level: 'product' | 'sku';
  file?: { name: string; contentType: string; base64: string };
  url?: string;
}

/** Chave da API pública (a chave completa só aparece na criação). */
export interface ApiKeyRow {
  id: string;
  name: string;
  prefix: string;
  scopes: string[];
  createdAt: string;
  createdBy: string | null;
  lastUsedAt: string | null;
  revokedAt: string | null;
}

export interface ApiKeyCreated extends ApiKeyRow {
  key: string;
}

// ---------- Pedidos ----------

export type OrderStatus = 'pending' | 'paid' | 'ready' | 'shipped' | 'delivered' | 'cancelled';

export interface OrderRow {
  id: string;
  channel: string;
  accountId: string;
  accountName: string | null;
  storeName: string | null;
  externalId: string;
  displayId?: string;
  packId: string | null;
  status: OrderStatus;
  channelStatus: string | null;
  createdAt: string;
  totalAmount: number | null;
  saleFee: number | null;
  buyerNickname: string | null;
  logisticType: string | null;
  /** Mercado Livre Instant (Envios Agora / Proximity): entrega em minutos. */
  isInstant?: boolean;
  /** Serviço de entrega como o canal chama (ex.: Prioritário, Entrega Rápida, Shopee Xpress). */
  shippingService?: string | null;
  shipmentSubstatus?: string | null;
  shipmentStatus: string | null;
  itemsCount: number;
  units: number;
  hasUnlinked: boolean;
  stockEffect: 'none' | 'deduct' | 'reserve';
  integrationStatus: 'none' | 'pending' | 'acked';
  invoiceStatus: 'received' | 'sent' | 'error' | null;
  /** Receita do medicamento (Mercado Livre Pharma): pendente de validação, aceita ou recusada. */
  prescription?: 'WAITING_VALIDATION' | 'ACCEPTED' | 'REJECTED' | null;
  firstItem: { title: string | null; sku: string | null; productId: string | null; thumbnail: string | null } | null;
  /** Carrinho (pack): quantos pedidos do canal formam esta compra (1 = pedido avulso). Valores da linha já somados. */
  ordersCount?: number;
  /** Carrinho que ganhou pedido depois que o ERP confirmou (o ERP criou o pedido sem esses itens); null = não. */
  packGrewAt?: string | null;
  /** Pedido para CNPJ segurado (a empresa não vende para CNPJ): cancelled = cancelado no canal; manual = cancelar à mão; error: … */
  cnpjHold?: { at: string; action: string } | null;
  /** Documento do comprador: CPF, CNPJ ou null (ainda não lido). */
  buyerDocType?: 'CPF' | 'CNPJ' | null;
}

export interface OrdersSummary {
  orders: number;
  units: number;
  revenue: number;
  fees: number;
  cancelled: number;
  unlinked: number;
  /** Carrinhos com o alerta "cresceu depois do ERP" ainda não resolvido (em qualquer data). */
  packGrew?: number;
  /** Pedidos para CNPJ que precisam ser cancelados à mão (a Berytus não conseguiu ou não cancela nesse canal). */
  cnpjManual?: number;
  /** Pedidos pagos há mais de 30 minutos (até 7 dias) ainda sem financeiro calculado. */
  noFinance?: number;
  /** Envios do Mercado Livre prontos com a etiqueta ainda não impressa (no canal/conta do filtro; últimos 30 dias). */
  unprinted?: number;
}

export interface OrdersPage extends Page<OrderRow> {
  summary: OrdersSummary;
}

export interface OrderItemRow {
  id: string;
  listingId: string | null;
  variationId: string | null;
  title: string | null;
  sellerSku: string | null;
  quantity: number;
  unitPrice: number | null;
  saleFee: number | null;
  listingType: string | null;
  product: { id: string; sku: string; title: string | null; isKit: boolean } | null;
  baseUnits: number | null;
  stockMoves: { kind: 'sale' | 'cancel'; quantity: number; at: string }[];
  /** Carrinho: nº no canal do pedido a que o item pertence. */
  orderExternalId?: string;
  /** Carrinho: o pedido deste item entrou depois que o ERP confirmou o carrinho (o ERP não tem este item). */
  lateInPack?: boolean;
}

export interface OrderFinance {
  source: string; final: boolean; gross: number | null; sellerDiscount: number | null; channelDiscount: number | null;
  commission: number | null; serviceFee: number | null; shipping: number | null; refund: number | null; otherFees: number | null;
  net: number | null; releasedAt: string | null; payoutAmount: number | null; updatedAt: string | null;
  lines: { label: string; amount: number; group: string }[];
}

export interface OrderDetail extends OrderRow {
  /** Financeiro ainda não calculado: o motivo da última tentativa (erro do canal), se houve. */
  financeError?: string | null;
  /** Extrato do canal (comissão, taxas, frete, descontos, líquido, repasse); null enquanto não lido. */
  finance?: OrderFinance | null;
  statusDetail: string | null;
  paidAmount: number | null;
  closedAt: string | null;
  updatedAt: string | null;
  buyerName: string | null;
  shipmentId: string | null;
  shipmentSubstatus: string | null;
  trackingNumber: string | null;
  shippedAt: string | null;
  deliveredAt: string | null;
  tags: string[];
  items: OrderItemRow[];
  permalink: string | null;
  handlingLimit: string | null;
  estimatedDelivery: string | null;
  shippingService: string | null;
  shippingPaidByBuyer: number | null;
  discountAmount: number | null;
  integration: { status: 'none' | 'pending' | 'acked'; erpOrderId: string | null; integratedAt: string | null };
  /**
   * Cancelamento pela loja no Mercado Livre: proximity (entrega rápida, pelo envio) ou feedback (venda não concretizada:
   * o comprador recebe o dinheiro de volta; conta como cancelamento na reputação).
   */
  meliCancel?: { mode: 'proximity' | 'feedback'; reasons: { value: string; label: string }[] } | null;
  ifood?: {
    displayId: string | null; orderType: string | null; scheduled: boolean; pickupCode: string | null; localizer: string | null; phone: string | null;
    observations: string[]; paymentPending: number; payments: { method: string | null; type: string | null; value: number | null }[];
  } | null;
  invoice: { status: 'received' | 'sent' | 'error'; accessKey: string; number: string | null; series: string | null; error: string | null; sentAt: string | null;
    /** Link do XML da nota (quando veio por link). */ xmlUrl: string | null; xmlSource: string | null; hasXml: boolean } | null;
  customer: { name: string | null; docType: string | null; docMasked: string | null; city: string | null; state: string | null; zip: string | null; ibge: string | null } | null;
  labelAvailable: boolean;
  labelDownloadedAt: string | null;
  /** Carrinho: os pedidos do canal que formam a compra (itens de todos em items, com orderExternalId). */
  packOrders?: { id: string; externalId: string; status: OrderStatus; totalAmount: number | null; saleFee: number | null; lateInPack?: boolean }[];
}

export interface OrdersSyncAccount {
  accountId: string;
  /** mercadolivre, shopee... */
  channel: string;
  name: string | null;
  liveFrom: string | null;
  syncedAt: string | null;
  orders: number;
  oldest: string | null;
  backfill: SyncJob | null;
}
export * from './roles.ts';

/**
 * Atributos padrão do sistema (Etapa C): somente leitura, alimentados pelo catálogo mestre (função system_attributes).
 * Prefixo berytus_ para não colidir com os atributos próprios do cliente, que continuam livres.
 */
export const SYSTEM_ATTRS: { key: string; label: string; dataType: 'boolean' | 'text' }[] = [
  { key: 'berytus_medicamento', label: 'Medicamento', dataType: 'boolean' },
  { key: 'berytus_mip', label: 'MIP', dataType: 'boolean' },
  { key: 'berytus_controlado', label: 'Controlado (Portaria 344)', dataType: 'boolean' },
  { key: 'berytus_lista_344', label: 'Lista da Portaria 344', dataType: 'text' },
  { key: 'berytus_psicotropico', label: 'Psicotrópico', dataType: 'boolean' },
  { key: 'berytus_antibiotico', label: 'Antibiótico', dataType: 'boolean' },
  { key: 'berytus_receita_retida', label: 'Receita retida', dataType: 'boolean' },
  { key: 'berytus_termolabil', label: 'Termolábil', dataType: 'boolean' },
  { key: 'berytus_pbm', label: 'PBM', dataType: 'boolean' },
  { key: 'berytus_categoria', label: 'Categoria Berytus', dataType: 'text' },
  { key: 'berytus_subcategoria', label: 'Subcategoria Berytus', dataType: 'text' },
];
export const SYSTEM_ATTR_PREFIX = 'berytus_';
export type SystemAttributes = Record<string, boolean | string | null>;
